import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import PlacementTest from "./components/PlacementTest";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import DashboardPage from "@/pages/DashboardPage";
import LevelSelectPage from "@/pages/LevelSelectPage";
import ReadingPage from "@/pages/ReadingPage";
import WritingPage from "@/pages/WritingPage";
import ListeningPage from "@/pages/ListeningPage";
import SpeakingPage from "@/pages/SpeakingPage";
import ProfilePage from "@/pages/ProfilePage";
import ExerciseDetailPage from "@/pages/ExerciseDetailPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import SubscriptionSuccessPage from "@/pages/SubscriptionSuccessPage";
import SubscriptionCancelPage from "@/pages/SubscriptionCancelPage";
import NotificationsPage from "@/pages/NotificationsPage";
import AdminPage from "@/pages/AdminPage";
import AffiliatePage from "@/pages/AffiliatePage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import BlogPage from "@/pages/BlogPage";
import BlogArticlePage from "@/pages/BlogArticlePage";

// Preview auto-login (temporary dev helper)
const PreviewAutoLogin = ({ target, level }) => {
  const { login, updateUserLevel } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    const doLogin = async () => {
      try {
        await login('examreadyenglishhelp@gmail.com', 'Admin123!');
        if (level) await updateUserLevel(level);
        navigate(target);
      } catch (e) {
        navigate('/auth');
      }
    };
    doLogin();
  }, []);
  return <div className="flex items-center justify-center min-h-screen"><p>Loading...</p></div>;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="animate-pulse text-slate-600">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// Referral Redirect Component
const ReferralRedirect = () => {
  const { code } = useParams();
  // Store the referral code and redirect to registration
  useEffect(() => {
    if (code) {
      localStorage.setItem('referral_code', code.toUpperCase());
    }
  }, [code]);
  return <Navigate to="/auth?mode=register" replace />;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="animate-pulse text-slate-600">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={
        <PublicRoute>
          <AuthPage />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/level-select" element={
        <ProtectedRoute>
          <LevelSelectPage />
        </ProtectedRoute>
      } />
      <Route path="/reading" element={
        <ProtectedRoute>
          <ReadingPage />
        </ProtectedRoute>
      } />
      <Route path="/writing" element={
        <ProtectedRoute>
          <WritingPage />
        </ProtectedRoute>
      } />
      <Route path="/listening" element={
        <ProtectedRoute>
          <ListeningPage />
        </ProtectedRoute>
      } />
      <Route path="/speaking" element={
        <ProtectedRoute>
          <SpeakingPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/exercise/:id" element={
        <ProtectedRoute>
          <ExerciseDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <SubscriptionPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription/cancel" element={
        <ProtectedRoute>
          <SubscriptionCancelPage />
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <NotificationsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
      <Route path="/affiliate" element={<AffiliatePage />} />
      <Route path="/preview/speaking" element={<PreviewAutoLogin target="/speaking" />} />
      <Route path="/preview/listening" element={<PreviewAutoLogin target="/listening" />} />
      <Route path="/preview/listening/b1" element={<PreviewAutoLogin target="/listening" level="B1" />} />
      <Route path="/preview/reading" element={<PreviewAutoLogin target="/reading" level="B1" />} />
      <Route path="/preview/reading/c1" element={<PreviewAutoLogin target="/reading" level="C1" />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />
      <Route path="/r/:code" element={<ReferralRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
